import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Timestamp } from '@angular/fire/firestore';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import {
  combineLatest,
  fromEvent,
  map,
  Observable,
  of,
  shareReplay,
  startWith,
  Subject,
  take,
  takeUntil,
} from 'rxjs';
import { Album } from '../../../models/album.model';
import { Group } from '../../../models/group.model';
import { AppException } from '../../../models/app-exception.model';
import { AlbumImage, LayoutHint } from '../../../models/image.album.model';
import { AlbumService } from '../../../services/album.service';
import { AuthService } from '../../../services/auth.service';
import { SchoolRolesService } from '../../../services/school-roles.service';
import { UtilsService } from '../../../services/utils.service';
import * as albumActions from '../../../state/actions/album.actions';
import * as albumImageActions from '../../../state/actions/image.album.actions';
import { AppState } from '../../../state/appstate';
import * as albumSelector from '../../../state/selectors/album.selector';
import * as groupSelector from '../../../state/selectors/group.selector';
import * as albumImageSelector from '../../../state/selectors/image.album.selector';
import { BtnBackComponent } from '../../components/buttons/btn-back/btn-back.component';
import {
  BtnFabComponent,
  BtnFabPosition,
} from '../../components/buttons/btn-fab/btn-fab.component';
import { BtnMoreComponent } from '../../components/buttons/btn-more/btn-more.component';

@Component({
  selector: 'app-view-album',
  standalone: true,
  imports: [BtnBackComponent, BtnMoreComponent, BtnFabComponent, AsyncPipe],
  templateUrl: './view-album.component.html',
  styleUrl: './view-album.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ViewAlbumComponent implements OnInit, OnDestroy {
  private cd = inject(ChangeDetectorRef);
  route: ActivatedRoute = inject(ActivatedRoute);
  roles: SchoolRolesService = inject(SchoolRolesService);
  private store: Store<AppState> = inject(Store<AppState>);
  private actions$ = inject(Actions);
  private utilsService: UtilsService = inject(UtilsService);

  toastr: ToastrService = inject(ToastrService);
  logger: NGXLogger = inject(NGXLogger);
  private dialog: MatDialog = inject(MatDialog);
  authService: AuthService = inject(AuthService);
  albumService: AlbumService = inject(AlbumService);

  album$: Observable<Album | undefined> = new Observable();
  albumImages$: Observable<AlbumImage[]> = new Observable();
  albumChunks$: Observable<AlbumImage[][]> = new Observable();
  albumColumns$: Observable<AlbumImage[][]> = new Observable();
  columnCount$: Observable<number> = new Observable();
  noOfImages = 10;

  loadingAlbum$: Observable<boolean> = new Observable();
  inProgress$ = new Observable<boolean>();
  loadedImages = new Set<string>();

  groupId: string | null = this.route.snapshot.paramMap.get('id');
  albumId: string | null = this.route.snapshot.paramMap.get('albumId');

  layoutHint = LayoutHint;
  btnFabPosition = BtnFabPosition.TOP;

  myRoles$: Observable<any> = new Observable();
  myRoles: any;
  group: Group | null | undefined;

  exit$ = new Subject<void>();

  constructor() {
    this.route.paramMap.pipe(takeUntil(this.exit$)).subscribe((params) => {
      this.groupId = params.get('id');
      this.albumId = params.get('albumId');
    });

    this.myRoles$ = this.store.select(
      groupSelector.getMyRolesInGroup(this.groupId),
    );
    this.myRoles$.pipe(takeUntil(this.exit$)).subscribe((roles) => {
      this.myRoles = roles;
    });

    this.store
      .select(groupSelector.getGroupById(this.groupId))
      .pipe(takeUntil(this.exit$))
      .subscribe((group) => {
        this.group = group;
      });

    this.album$ = this.store
      .select(albumSelector.getAlbumById(this.albumId!))
      .pipe(takeUntil(this.exit$));

    this.loadingAlbum$ = this.store
      .select(albumImageSelector.loading)
      .pipe(takeUntil(this.exit$));

    this.albumImages$ = this.store
      .select(albumImageSelector.getAlbumImagesByAlbumId(this.albumId!))
      .pipe(takeUntil(this.exit$));

    this.columnCount$ = fromEvent(window, 'resize').pipe(
      startWith(null),
      map(() => {
        const width = window.innerWidth;
        if (width < 640) return 1;
        if (width < 1024) return 2;
        if (width < 1280) return 3;
        return 4;
      }),
      shareReplay(1),
    );

    // 2. Combine the images and the column count to create the "buckets"
    this.albumColumns$ = combineLatest([
      this.albumImages$,
      this.columnCount$,
    ]).pipe(
      map(([images, count]) => {
        const columns: AlbumImage[][] = Array.from({ length: count }, () => []);
        images.forEach((img, index) => {
          columns[index % count].push(img);
        });
        return columns;
      }),
    );
  }

  ngOnInit(): void {
    this.albumImages$.pipe(take(1)).subscribe((images) => {
      if (images.length === 0) {
        this.logger.debug(
          'No images found in store for album. Fetching from server...',
          this.albumId,
        );
        if (this.groupId && this.albumId) {
          this.store.dispatch(
            albumImageActions.getAlbumImages({
              groupId: this.groupId,
              albumId: this.albumId,
              limitRecords: this.noOfImages,
            }),
          );
        }
      }
    });

    this.actions$
      .pipe(ofType(albumImageActions.deleteImage), takeUntil(this.exit$))
      .subscribe((action) => {
        this.inProgress$ = of(true);
      });

    this.actions$
      .pipe(ofType(albumImageActions.deleteSuccess), takeUntil(this.exit$))
      .subscribe((action) => {
        this.inProgress$ = of(false);
        this.toastr.success('Image deleted successfully', 'Delete Successful');
      });

    this.actions$
      .pipe(ofType(albumImageActions.deleteFailure), takeUntil(this.exit$))
      .subscribe((action) => {
        this.inProgress$ = of(false);
        this.toastr.error(
          'Failed to delete image. Please try again.',
          'Delete Failed',
        );
      });

    this.actions$
      .pipe(ofType(albumActions.updateFailure), takeUntil(this.exit$))
      .subscribe((action) => {
        this.logger.error('action=updateFailure', action.error);
        this.utilsService.onShowAlert(action.error as AppException, false);
        this.loadingAlbum$ = of(false);
      });

    this.actions$
      .pipe(ofType(albumActions.updateSuccess), takeUntil(this.exit$))
      .subscribe((action) => {
        this.toastr.success('Album updated successfully', 'Update Successful');
        this.loadingAlbum$ = of(false);
      });
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  onImageLoad(imageId: string) {
    if (this.loadedImages.has(imageId)) return;
    setTimeout(() => {
      this.loadedImages.add(imageId);
      this.cd.markForCheck();
    });
  }

  addMoreImages() {
    this.logger.debug('Add more images to album', this.albumId);
    this.albumImages$.pipe(take(1)).subscribe((images) => {
      const lastImageDate =
        images.length > 0 ? images[images.length - 1].created : Timestamp.now();
      if (this.groupId && this.albumId) {
        this.store.dispatch(
          albumImageActions.getAlbumImages({
            groupId: this.groupId,
            albumId: this.albumId,
            lastRecord: lastImageDate,
            limitRecords: this.noOfImages,
          }),
        );
      }
    });
  }

  deleteImage(image: AlbumImage) {
    this.utilsService
      .onRequestConfirmation({
        title: 'Delete Image',
        description:
          'Are you sure you want to delete this image? This action cannot be undone.',
        action: 'Delete',
      })
      .pipe(take(1))
      .subscribe((confirmed) => {
        if (confirmed) {
          this.logger.debug('User confirmed image deletion', image.id);
          this.store.dispatch(
            albumImageActions.deleteImage({
              groupId: this.groupId!,
              albumId: this.albumId!,
              imageId: image.id!,
            }),
          );
        } else {
          this.logger.debug('User cancelled image deletion');
        }
      });
  }

  setAsCover(image: AlbumImage) {
    this.loadingAlbum$ = of(true);

    this.store.dispatch(
      albumActions.updateAlbum({
        id: image.albumId!,
        album: {
          albumCoverUrl: image.variants?.mobile,
          modified: Timestamp.now(),
        },
      }),
    );
  }

  showImage(image: AlbumImage) {
    // this.utilsService.onShowImageViewer({
    //   images: [image],
    //   startIndex: 0,
    // });
  }

  canManageCover(): boolean {
    if (this.group?.groupType === 'church') {
      return this.myRoles?.admin === true;
    }
    return this.myRoles?.management === true;
  }
}
